import React from 'react';
import { StandardTableActions, ColoredBadge } from '../../components/ui';
import { Column } from '../../components/DataTable/DataTable';
import { FormSection } from '../../components/Form';
import { Supplier } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';

const formatCurrency = (amount?: number | null) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const SUPPLIER_STATUS_COLOR_ACTIVE = '#22c55e';
const SUPPLIER_STATUS_COLOR_INACTIVE = '#ef4444';

const renderSupplierStatusBadge = (isActive?: boolean, className?: string) => (
  <ColoredBadge
    label={isActive ? 'Active' : 'Inactive'}
    color={isActive ? SUPPLIER_STATUS_COLOR_ACTIVE : SUPPLIER_STATUS_COLOR_INACTIVE}
    className={className}
  />
);

export const moduleConfig = {
  module: {
    title: 'Suppliers',
    description: 'Manage your supplier database',
    permissionModuleKey: 'suppliers',
    route: {
      list: '/suppliers',
      new: '/suppliers/new',
      detail: (id: number) => `/suppliers/${id}`,
      edit: (id: number) => `/suppliers/${id}/edit`,
    },
    api: {
      list: '/suppliers',
      detail: (id: number) => `/suppliers/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search suppliers by name, email, or phone...',
  },

  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['name', 'name2', 'contactPerson', 'isActive'] },
    { name: 'contact', label: 'Contact', icon: '☎️', fields: ['email', 'email2', 'phone', 'phone2', 'website'] },
    { name: 'location', label: 'Location', icon: '📍', fields: ['address', 'city', 'zipCode', 'state', 'countryId'] },
    { name: 'business', label: 'Business', icon: '💼', fields: ['vatNumber', 'paymentTerms', 'taxAuthorityId'] },
  ],

  filters: {
    fields: [
      { key: 'name', label: 'Name', placeholder: 'All Name', getValue: (item: Supplier) => item.name, options: [] },
      { key: 'name2', label: 'Name 2', placeholder: 'All Name 2', getValue: (item: Supplier) => item.name2, options: [] },
      { key: 'contactPerson', label: 'Contact Person', placeholder: 'All Contact', getValue: (item: Supplier) => item.contactPerson, options: [] },
      { key: 'email', label: 'Email', placeholder: 'All Email', getValue: (item: Supplier) => item.email, options: [] },
      { key: 'phone', label: 'Phone', placeholder: 'All Phone', getValue: (item: Supplier) => item.phone, options: [] },
      { key: 'website', label: 'Website', placeholder: 'All Website', getValue: (item: Supplier) => item.website, options: [] },
      { key: 'address', label: 'Address', placeholder: 'Address...', type: 'text' as const, getValue: (item: Supplier) => item.address, options: [] },
      { key: 'city', label: 'City', placeholder: 'All City', getValue: (item: Supplier) => item.city, options: [] },
      { key: 'zipCode', label: 'Zip Code', placeholder: 'All Zip', getValue: (item: Supplier) => item.zipCode, options: [] },
      { key: 'state', label: 'State', placeholder: 'All State', getValue: (item: Supplier) => item.state, options: [] },
      { key: 'countryId', label: 'Country', placeholder: 'All Country', getValue: (item: Supplier) => item.countryId?.toString(), options: [], apiEndpoint: '/p-country', labelField: 'pc_descr' },
      { key: 'vatNumber', label: 'VAT Number', placeholder: 'All VAT', getValue: (item: Supplier) => item.vatNumber, options: [] },
      { key: 'paymentTerms', label: 'Payment Terms', placeholder: 'All', getValue: (item: Supplier) => item.paymentTerms?.toString(), options: [] },
      { key: 'taxAuthorityId', label: 'Tax Authority', placeholder: 'All', getValue: (item: Supplier) => item.taxAuthorityId?.toString(), options: [], apiEndpoint: '/p-tax-authority', labelField: 'pt_descr' },
      { key: 'isActive', label: 'Status', placeholder: 'All Status', type: 'boolean' as const, getValue: (item: Supplier) => item.isActive ? 'Active' : 'Inactive', options: ['Active', 'Inactive'] },
    ],
    applyFilter: (
      items: Supplier[],
      filters: Record<string, string[] | string>,
      _dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ): Supplier[] => {
      const toArr = (v: string[] | string | undefined): string[] =>
        v == null ? [] : Array.isArray(v) ? v : v ? [v] : [];
      return items.filter((item) => {
        const str = (v: unknown) => (v == null ? '' : String(v)).toLowerCase();
        const nameArr = toArr(filters.name);
        if (nameArr.length && !nameArr.some((f: string) => str(item.name).includes(str(f)))) return false;
        const name2Arr = toArr(filters.name2);
        if (name2Arr.length && !name2Arr.some((f: string) => str(item.name2).includes(str(f)))) return false;
        const contactArr = toArr(filters.contactPerson);
        if (contactArr.length && !contactArr.some((f: string) => str(item.contactPerson).includes(str(f)))) return false;
        const emailArr = toArr(filters.email);
        if (emailArr.length && !emailArr.some((f: string) => str(item.email).includes(str(f)))) return false;
        const phoneArr = toArr(filters.phone);
        if (phoneArr.length && !phoneArr.some((f: string) => str(item.phone).includes(str(f)))) return false;
        const websiteArr = toArr(filters.website);
        if (websiteArr.length && !websiteArr.some((f: string) => str(item.website).includes(str(f)))) return false;
        const addr = typeof filters.address === 'string' ? filters.address : (filters.address as string[])?.[0];
        if (addr?.trim() && !str(item.address).includes(str(addr))) return false;
        const cityArr = toArr(filters.city);
        if (cityArr.length && !cityArr.some((f: string) => str(item.city).includes(str(f)))) return false;
        const zipArr = toArr(filters.zipCode);
        if (zipArr.length && !zipArr.some((f: string) => str(item.zipCode).includes(str(f)))) return false;
        const stateArr = toArr(filters.state);
        if (stateArr.length && !stateArr.some((f: string) => str(item.state).includes(str(f)))) return false;
        const countryArr = toArr(filters.countryId);
        if (countryArr.length && !countryArr.includes(String(item.countryId))) return false;
        const vatArr = toArr(filters.vatNumber);
        if (vatArr.length && !vatArr.some((f: string) => str(item.vatNumber).includes(str(f)))) return false;
        const paymentArr = toArr(filters.paymentTerms);
        if (paymentArr.length && !paymentArr.includes(String(item.paymentTerms))) return false;
        const taxArr = toArr(filters.taxAuthorityId);
        if (taxArr.length && !taxArr.includes(String(item.taxAuthorityId))) return false;
        const isActiveArr = toArr(filters.isActive);
        if (isActiveArr.length) {
          const status = item.isActive ? 'Active' : 'Inactive';
          if (!isActiveArr.includes(status)) return false;
        }
        return true;
      });
    },
  },

  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add Supplier',
        showTitle: false,
        variant: 'primary' as const,
        href: '/suppliers/new',
      },
    ],
  },

  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Supplier) => {
      window.location.href = `/suppliers/${item.id}`;
    },
    keyExtractor: (item: Supplier) => item.id,
  },

  emptyStates: {
    noData: 'Get started by creating a new supplier.',
    noSearchResults: 'No suppliers found matching your search.',
    noFilterResults: 'No suppliers match the selected filters.',
  },

  delete: {
    title: 'Delete Supplier',
    message: 'Are you sure you want to delete this supplier? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete supplier. Please try again.',
    handler: async (id: number) => {
      const { deleteSupplier } = await import('../../services/mutations');
      await deleteSupplier(id);
    },
  },

  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '-'),
  },

  detail: {
    subtitle: 'Supplier Details',
    notFoundTitle: 'Supplier not found',
    notFoundMessage: "The supplier you're looking for doesn't exist.",
    notFoundBackText: 'Back to Suppliers',
    defaultTitle: 'Unnamed Supplier',
    getTitle: (item: Supplier) => item.name || 'Unnamed Supplier',
  },

  form: {
    new: { title: 'New Supplier', subtitle: 'Add a new supplier', submitText: 'Create Supplier' },
    edit: { title: 'Edit Supplier', subtitle: 'Update supplier information', submitText: 'Update Supplier' },
    cancelText: 'Cancel',
    loadingText: 'Loading supplier...',
    errorMessage: 'Failed to save supplier. Please try again.',
    supportsFiles: false,
    create: async (data: Partial<Supplier>) => {
      const { createSupplier } = await import('../../services/mutations');
      return await createSupplier(data);
    },
    update: async (id: number, data: Partial<Supplier>) => {
      const { updateSupplier } = await import('../../services/mutations');
      return await updateSupplier(id, data);
    },
  },

  formLayout: {
    mode: 'default' as const,
    sections: [
      { id: 'basic', title: 'Basic', icon: '📄', gridCols: 'md:grid-cols-2', fields: ['name', 'name2', 'contactPerson'], collapsible: true },
      { id: 'contact', title: 'Contact', icon: '☎️', gridCols: 'md:grid-cols-2', fields: ['email', 'email2', 'phone', 'phone2', 'website'], collapsible: true },
      { id: 'address', title: 'Address', icon: '📍', gridCols: 'md:grid-cols-2', fields: ['address', 'city', 'zipCode', 'state', 'countryId'], collapsible: true },
      { id: 'business', title: 'Business', icon: '💼', gridCols: 'md:grid-cols-2', fields: ['vatNumber', 'paymentTerms', 'taxAuthorityId', 'notes', 'isActive'], collapsible: true },
    ],
  },

  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Enter supplier name' },
    { name: 'name2', label: 'Name 2', type: 'text', placeholder: 'Optional' },
    { name: 'contactPerson', label: 'Contact Person', type: 'text', placeholder: 'Contact name' },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'Email' },
    { name: 'email2', label: 'Email 2', type: 'email', placeholder: 'Secondary email' },
    { name: 'phone', label: 'Phone', type: 'tel', placeholder: 'Phone' },
    { name: 'phone2', label: 'Phone 2', type: 'tel', placeholder: 'Secondary phone' },
    { name: 'website', label: 'Website', type: 'url', placeholder: 'https://' },
    { name: 'address', label: 'Address', type: 'text', placeholder: 'Street address' },
    { name: 'city', label: 'City', type: 'text', placeholder: 'City' },
    { name: 'zipCode', label: 'Zip Code', type: 'text', placeholder: 'Zip' },
    { name: 'state', label: 'State', type: 'text', placeholder: 'State' },
    { name: 'countryId', label: 'Country', type: 'select', placeholder: 'Select country', apiEndpoint: '/p-country', labelField: 'pc_descr', valueField: 'id' },
    { name: 'vatNumber', label: 'VAT Number', type: 'text', placeholder: 'VAT' },
    { name: 'paymentTerms', label: 'Payment Terms', type: 'number', placeholder: 'Days' },
    { name: 'taxAuthorityId', label: 'Tax Authority', type: 'select', placeholder: 'Select', apiEndpoint: '/p-tax-authority', labelField: 'pt_descr', valueField: 'id' },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Notes' },
    { name: 'isActive', label: 'Active', type: 'checkbox' },
  ],
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Supplier>[] => [
  {
    key: 'name',
    header: 'Supplier',
    sortable: true,
    render: (value, row) => (
      <div>
        <div className="font-medium text-gray-900 dark:text-gray-100">
          {(row.name || 'Unnamed').length > 50 ? (row.name || '').substring(0, 50) + '...' : row.name || 'Unnamed'}
        </div>
        {(row.name2 || row.contactPerson) && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {[row.name2, row.contactPerson].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'totalTurnover',
    header: 'Total Turnover',
    sortable: true,
    render: (_, row) => formatCurrency(row.totalTurnover),
  },
  {
    key: 'currentYearTurnover',
    header: 'Current Year',
    sortable: true,
    render: (_, row) => formatCurrency(row.currentYearTurnover),
  },
  {
    key: 'lastInvoiceDate',
    header: 'Last Invoice',
    sortable: true,
    render: (_, row) => (
      <div>
        <div>{row.lastInvoiceDate ? formatDate(row.lastInvoiceDate) : '-'}</div>
        {row.lastInvoiceAmount != null && (
          <div className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(row.lastInvoiceAmount)}</div>
        )}
      </div>
    ),
  },
  { key: 'contactPerson', header: 'Contact', sortable: true, render: (v) => v || '-' },
  {
    key: 'email',
    header: 'Email',
    sortable: true,
    render: (v, row) => (
      <div>
        {v ? (
          <a href={`mailto:${v}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">
            {v}
          </a>
        ) : (
          '-'
        )}
        {row?.email2 && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            <a href={`mailto:${row.email2}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">
              {row.email2}
            </a>
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'phone',
    header: 'Phone',
    sortable: true,
    render: (v, row) => (
      <div>
        {v ? (
          <a href={`tel:${v}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">
            {v}
          </a>
        ) : (
          '-'
        )}
        {row?.phone2 && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{row.phone2}</div>
        )}
      </div>
    ),
  },
  { key: 'city', header: 'City', sortable: true, render: (v) => v || '-' },
  {
    key: 'isActive',
    header: 'Status',
    sortable: true,
    render: (_, row) => renderSupplierStatusBadge(row.isActive),
  },
  { key: 'createdAt', header: 'Created', sortable: true, render: (_, row) => formatDate(row.createdAt) },
];

export const getDetailSections = (item: Supplier, formatDate: (dateString?: string) => string) => [
  {
    title: 'Basic Information',
    fields: [
      { label: 'Name', value: item.name || '-' },
      { label: 'Name 2', value: item.name2 || '-', condition: !!item.name2 },
      { label: 'Contact Person', value: item.contactPerson || '-', condition: !!item.contactPerson },
      { label: 'Status', value: renderSupplierStatusBadge(item.isActive) },
      { label: 'VAT Number', value: item.vatNumber || '-', condition: !!item.vatNumber },
    ],
  },
  {
    title: 'Contact Information',
    fields: [
      { label: 'Email', value: item.email ? <a href={`mailto:${item.email}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">{item.email}</a> : '-', condition: !!item.email },
      { label: 'Email 2', value: item.email2 ? <a href={`mailto:${item.email2}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">{item.email2}</a> : '-', condition: !!item.email2 },
      { label: 'Phone', value: item.phone ? <a href={`tel:${item.phone}`} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">{item.phone}</a> : '-', condition: !!item.phone },
      { label: 'Phone 2', value: item.phone2 || '-', condition: !!item.phone2 },
      { label: 'Website', value: item.website ? <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300">{item.website}</a> : '-', condition: !!item.website },
    ],
  },
  {
    title: 'Address',
    fields: [
      { label: 'Address', value: item.address || '-', condition: !!item.address },
      { label: 'City', value: item.city || '-', condition: !!item.city },
      { label: 'Zip Code', value: item.zipCode || '-', condition: !!item.zipCode },
      { label: 'State', value: item.state || '-', condition: !!item.state },
    ],
    condition: !!(item.address || item.city || item.zipCode || item.state),
  },
  {
    title: 'Additional',
    fields: [
      { label: 'Notes', value: item.notes ? <span className="whitespace-pre-wrap">{item.notes}</span> : '-', condition: !!item.notes },
      { label: 'Payment Terms', value: item.paymentTerms != null ? `${item.paymentTerms} days` : '-', condition: item.paymentTerms != null },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt), condition: !!item.updatedAt },
    ],
  },
];

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

export const getTableActions = (
  item: Supplier,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getFormSections = (): FormSection[] => [
  { title: 'Basic', gridCols: 2, fields: [{ name: 'name', label: 'Name', type: 'text', required: true }, { name: 'name2', label: 'Name 2', type: 'text' }, { name: 'contactPerson', label: 'Contact Person', type: 'text' }] },
  { title: 'Contact', gridCols: 2, fields: [{ name: 'email', label: 'Email', type: 'email' }, { name: 'email2', label: 'Email 2', type: 'email' }, { name: 'phone', label: 'Phone', type: 'tel' }, { name: 'phone2', label: 'Phone 2', type: 'tel' }, { name: 'website', label: 'Website', type: 'url' }] },
  { title: 'Address', gridCols: 2, fields: [{ name: 'address', label: 'Address', type: 'text', colSpan: 2 }, { name: 'city', label: 'City', type: 'text' }, { name: 'zipCode', label: 'Zip Code', type: 'text' }, { name: 'state', label: 'State', type: 'text' }, { name: 'countryId', label: 'Country', type: 'text' }] },
  { title: 'Business', gridCols: 2, fields: [{ name: 'vatNumber', label: 'VAT Number', type: 'text' }, { name: 'paymentTerms', label: 'Payment Terms', type: 'number' }, { name: 'taxAuthorityId', label: 'Tax Authority', type: 'text' }, { name: 'notes', label: 'Notes', type: 'textarea', colSpan: 2 }, { name: 'isActive', label: 'Active', type: 'checkbox' }] },
];

export const getInitialFormData = (): Partial<Supplier> => ({
  name: '',
  name2: '',
  contactPerson: '',
  address: '',
  city: '',
  zipCode: '',
  state: '',
  website: '',
  phone: '',
  phone2: '',
  email: '',
  email2: '',
  notes: '',
  vatNumber: '',
  paymentTerms: undefined,
  taxAuthorityId: undefined,
  isActive: true,
});

export const getStatusBadges = (item: Supplier, _colors?: unknown): React.ReactNode =>
  renderSupplierStatusBadge(item.isActive);

export const mapItemToFormData = (item: Supplier): Partial<Supplier> => ({
  name: item.name || '',
  name2: item.name2 || '',
  contactPerson: item.contactPerson || '',
  address: item.address || '',
  city: item.city || '',
  zipCode: item.zipCode || '',
  state: item.state || '',
  countryId: item.countryId,
  website: item.website || '',
  phone: item.phone || '',
  phone2: item.phone2 || '',
  email: item.email || '',
  email2: item.email2 || '',
  notes: item.notes || '',
  vatNumber: item.vatNumber || '',
  paymentTerms: item.paymentTerms,
  taxAuthorityId: item.taxAuthorityId,
  isActive: item.isActive,
});

export type ModuleConfig = typeof moduleConfig;
